import {
  getParsedCrudData,
  getTranslatedText,
  reactSelectInlineError
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, isEmpty, omit, random } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { FormattedMessage, useIntl } from 'react-intl';
import { IndicatorSeparator } from '@nile/banyan/src/Pebbles/select';
import { DropdownArrow } from '@nile/mangifera/src/Pebbles/select';
import ClientSiteSelection from '../Pages/Invoice/ClientSiteSelection';

const Wrapper = styled.div`
  .select-container .container:first-child {
    margin: 0;
  }

  display: flex;
  align-items: center;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  padding: 20px;

  /* Responsive width */
  @media (max-width: 1279px) {
    width: ${(props) => (props.responsive ? '400px' : '100%')};
  }

  @media (min-width: 1280px) and (max-width: 1600px) {
    width: ${(props) => (props.responsive ? '440px' : '100%')};
  }
  @media (max-width: 1600px) {
    .site-custom-class {
      margin-left: -10px;
    }
  }
`;

const PrimarySiteContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  max-width: 600px;
  align-items: flex-end;

  /* Responsive alignment */
  @media (max-width: 1536px) {
    align-items: ${(props) => (props.responsive ? 'flex-start' : 'flex-end')};
  }
`;

const MultiClientSelector = ({
  clientList,
  updateClientList,
  clientsToFilter,
  accountId,
  disableClientEdit,
  disableSiteEdit,
  enableMultiSelectSites = false,
  actionType,
  errors,
  responsive = false,
  checkIfClientNull
}) => {
  const intl = useIntl();
  function addClient() {
    if (checkIfClientNull()) {
      return;
    }
    let clonedClientsArray = cloneDeep(clientList);
    clonedClientsArray.push({
      id: random(0, 1000).toString(),
      client: null,
      site: null
    });
    updateClientList(clonedClientsArray);
  }

  function removeClient(data) {
    const updatedClientList = clientList.filter(
      (comp) =>
        comp?.client?.accountId !== data?.client?.accountId ||
        (!comp?.client?.accountId && comp?.id !== data?.id)
    );
    updateClientList(updatedClientList);
  }

  useEffect(() => {
    if (clientList.length === 0) {
      if (isEmpty(accountId)) {
        addClient();
      } else {
        let clonedClientsArray = cloneDeep(clientList);
        clonedClientsArray.push({
          id: random(0, 1000).toString(),
          client: { accountId: accountId },
          site: null
        });
        updateClientList(clonedClientsArray);
      }
    }
  }, []);

  const groupClientList = useMemo(() => {
    if (enableMultiSelectSites) {
      const acc = [];
      clientList.forEach((curr) => {
        const key = curr.client?.accountId || curr.id;
        let group = acc.find((g) => (g.client?.accountId || g.id) === key);
        if (!group) {
          group = {
            id: curr.id,
            client: curr.client,
            site: [],
            primarySite: curr?.primarySite
          };
          acc.push(group);
        }
        if (curr.site) {
          const s = {
            ...curr.site,
            value: curr.site.name,
            label: curr.site.name
          };

          if (actionType === 'edit') {
            s.associationId = curr.id;
          }
          group.site.push(s);
        }
      });
      return acc;
    }
    return clientList;
  }, [clientList, enableMultiSelectSites]);

  function updateClientProperties(id, args) {
    const clonedClientsArray = cloneDeep(clientList);

    const { client, site: sitesArray = [] } = args.properties;

    const baseSitesArray =
      groupClientList.find((el) => el.client?.accountId === client?.accountId)
        ?.site || [];

    const {
      addedItems: addedSites,
      deletedItems: deletedSites
    } = getParsedCrudData(baseSitesArray, sitesArray, 'id');

    if (baseSitesArray.length > 1 || sitesArray?.length > 1) {
      console.log(addedSites, 'addedSites', deletedSites, 'deletedSites');

      let updatedClientList = clonedClientsArray;

      if (addedSites?.length) {
        updatedClientList.push({
          id: random(0, 1000).toString(),
          client: client,
          site: addedSites?.[0],
          primarySite: false
        });
      }

      if (deletedSites?.length) {
        updatedClientList = updatedClientList.filter(
          (el) => el.site?.id !== deletedSites?.[0]?.id
        );
      }

      updateClientList(updatedClientList);
    } else {
      const clientExist = clonedClientsArray.find(
        (el) =>
          el.id === id &&
          (el.client === null || el.client?.accountId === client?.accountId)
      );
      let updatedClientList = clonedClientsArray;
      if (!clientExist) {
        // Remove all existing associations with the same client
        const removedClientId = updatedClientList.find((el) => el.id === id)
          ?.client?.accountId;
        updatedClientList = updatedClientList.filter(
          (el) => el.client?.accountId !== removedClientId
        );
        updatedClientList.push({
          id: random(0, 1000).toString(),
          client: client,
          site: sitesArray?.[0] ? omit(sitesArray?.[0], ['hq']) : null
        });
      } else {
        updatedClientList = updatedClientList.map((el) => {
          if (el.id === id) {
            return {
              ...el,
              client: client,
              site: sitesArray?.[0] ? omit(sitesArray?.[0], ['hq']) : null
            };
          }
          return el;
        });
      }
      updateClientList(updatedClientList);
    }
  }

  return (
    <div className="fx col">
      <div className="grid">
        {groupClientList.map((data, i) => {
          const { client: selectedClient, site: selectedSite, id } = data;

          const primarySiteId =
            clientList.find(
              (el) =>
                el.client?.accountId === selectedClient?.accountId &&
                el.primarySite
            )?.site?.id || null;

          return (
            <Wrapper key={id} className="fx actr" responsive={responsive}>
              <div className="w100 fx col g20">
                <ClientSiteSelection
                  disableClearable
                  site={selectedSite}
                  client={selectedClient}
                  clientsToFilter={clientsToFilter}
                  updateProperties={updateClientProperties.bind(null, id)}
                  hideLabel={false}
                  disableClientEdit={i === 0 && disableClientEdit}
                  disableSiteEdit={i === 0 && disableSiteEdit}
                  enableMultiSelectSites={enableMultiSelectSites}
                  errors={errors}
                />
                {enableMultiSelectSites && (
                  <PrimarySiteContainer responsive={responsive}>
                    <div className="Polaris-Label w280">
                      <label
                        id="userPhoneLabel"
                        htmlFor="userPhone"
                        className="Polaris-Label__Text"
                      >
                        <FormattedMessage id="common.primary.site" />
                      </label>
                    </div>
                    <Select
                      className="w280"
                      classNamePrefix="value_selector"
                      components={{ IndicatorSeparator, DropdownArrow }}
                      placeholder={getTranslatedText(
                        intl,
                        'ticket.newProject.details.site.pl'
                      )}
                      value={
                        selectedSite?.find((el) => el.id === primarySiteId) ||
                        null
                      }
                      options={selectedSite?.map((el) => ({
                        ...el,
                        value: el.name,
                        label: el.name
                      }))}
                      onChange={(val) => {
                        const clonedClientList = cloneDeep(clientList);
                        const updatedClientList = clonedClientList.map((el) => {
                          if (
                            selectedClient?.accountId === el.client?.accountId
                          ) {
                            return {
                              ...el,
                              primarySite: el.site?.id === val?.id
                            };
                          }
                          return el;
                        });
                        updateClientList(updatedClientList);
                      }}
                      isDisabled={selectedSite?.length === 0 || !selectedSite}
                    />
                    <div className="w300">
                      {!isEmpty(errors) &&
                        selectedSite?.length > 0 &&
                        !primarySiteId &&
                        reactSelectInlineError(
                          getTranslatedText(intl, 'common.primary.site.error')
                        )}
                    </div>
                  </PrimarySiteContainer>
                )}
              </div>
              {groupClientList?.length > 1 && (
                <div
                  className="remove-client cursor-pointer ml-10"
                  id={data.id}
                  onClick={() => removeClient(data)}
                >
                  <SVGIcon name="SquareMinus" size="sm" />
                </div>
              )}
            </Wrapper>
          );
        })}
      </div>
      <div
        className="add-client link-text-color cursor-pointer mb20 l100 ml-10 fx actr g8"
        onClick={addClient}
      >
        <SVGIcon name="Add" size="sm" color="#091EF5" />
        <FormattedMessage id="invoiceAudit.group.clientAndSite" />
      </div>
    </div>
  );
};

export default MultiClientSelector;
